import { useState } from "react";
import { MapPin, Calendar, Clock, Users, Send, Phone } from "lucide-react";
import { useLang, waLink, LOCATIONS, PHONE_TEL } from "@/i18n";

export const QuickBooking = () => {
    const { t, lang } = useLang();
    const [form, setForm] = useState({
        from: LOCATIONS[0],
        to: LOCATIONS[1],
        date: "",
        time: "",
        pax: "1",
    });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = (e) => {
        e.preventDefault();
        const msg =
            lang === "tr"
                ? `Merhaba, rezervasyon talebim:\n• Nereden: ${form.from}\n• Nereye: ${form.to}\n• Tarih: ${form.date || "-"}\n• Saat: ${form.time || "-"}\n• Kişi: ${form.pax}`
                : `Hello, booking request:\n• Pickup: ${form.from}\n• Drop-off: ${form.to}\n• Date: ${form.date || "-"}\n• Time: ${form.time || "-"}\n• Passengers: ${form.pax}`;
        window.open(waLink(msg), "_blank", "noopener");
    };

    const fieldCls =
        "w-full bg-[#0F0F13] border border-white/10 focus:border-[#D4AF37]/60 rounded-lg px-4 py-3 pl-10 text-sm text-[#F7F5F0] outline-none transition-colors duration-300 ff-sans appearance-none";

    return (
        <section
            id="rezervasyon"
            data-testid="quick-reservation-widget"
            className="relative z-20 max-w-6xl mx-auto px-5 lg:px-8 -mt-20"
        >
            <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#0F0F13]/90 backdrop-blur-xl p-6 md:p-8 shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
                <div className="mb-6">
                    <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-2">
                        {t("booking.title")}
                    </span>
                    <p className="text-sm text-zinc-400 ff-sans font-light">{t("booking.sub")}</p>
                </div>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <label className="relative block" data-testid="booking-from-field">
                        <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
                        <select className={fieldCls} value={form.from} onChange={(e) => set("from", e.target.value)} data-testid="booking-from-select" aria-label={t("booking.from")}>
                            {LOCATIONS.map((l) => (
                                <option key={l} value={l}>{l}</option>
                            ))}
                        </select>
                    </label>
                    <label className="relative block" data-testid="booking-to-field">
                        <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
                        <select className={fieldCls} value={form.to} onChange={(e) => set("to", e.target.value)} data-testid="booking-to-select" aria-label={t("booking.to")}>
                            {LOCATIONS.map((l) => (
                                <option key={l} value={l}>{l}</option>
                            ))}
                        </select>
                    </label>
                    <label className="relative block" data-testid="booking-date-field">
                        <Calendar size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70 pointer-events-none" />
                        <input type="date" className={`${fieldCls} [color-scheme:dark]`} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="booking-date-input" aria-label={t("booking.date")} />
                    </label>
                    <label className="relative block" data-testid="booking-time-field">
                        <Clock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70 pointer-events-none" />
                        <input type="time" className={`${fieldCls} [color-scheme:dark]`} value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="booking-time-input" aria-label={t("booking.time")} />
                    </label>
                    <label className="relative block" data-testid="booking-pax-field">
                        <Users size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]/70" />
                        <select className={fieldCls} value={form.pax} onChange={(e) => set("pax", e.target.value)} data-testid="booking-pax-select" aria-label={t("booking.pax")}>
                            {["1", "2", "3", "4"].map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </label>
                    <div className="sm:col-span-2 lg:col-span-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-1">
                        <button
                            type="submit"
                            data-testid="booking-submit-button"
                            className="gold-glow-hover flex-1 flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#08080A] font-semibold py-3.5 px-6 ff-sans text-sm tracking-wide"
                        >
                            <Send size={15} /> {t("booking.send")}
                        </button>
                        <span className="text-zinc-500 text-xs text-center ff-sans">{t("booking.or")}</span>
                        <a
                            href={PHONE_TEL}
                            data-testid="booking-call-button"
                            className="gold-glow-hover flex-1 flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#E6C687] py-3.5 px-6 ff-sans text-sm tracking-wide"
                        >
                            <Phone size={15} /> {t("booking.callNow")}
                        </a>
                    </div>
                </form>
            </div>
        </section>
    );
};
